import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { fmtUsd, fmtUsdSigned } from '@/components/cards/cardFormat';
import { apiFetch } from '@/stores/authStore';
import { useTickerFeedNeeds } from '@/hooks/useTickerFeedNeeds';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { polyPositionLiveData } from '@/lib/polymarket/describeOrder';
import { estimateAggregateFeeCents, takerRateForTicker } from '@/lib/pricing/fees';
import { estimatePolyOrderFeeCents } from '@/lib/pricing/polyFees';
import { usePolyFeeSchedules } from '@/hooks/usePolyFeeSchedules';
import type { PolyFeeSchedule } from '@/lib/polymarket/relay';
import { InfoTooltip } from '@/components/cards/cardKit';
import { useUsOrgRows } from '@/views/OrgDetail/useUsOrgRows';
import { addKnown, usOrgMemberEconomics } from '@/views/OrgDetail/orgUsEconomics';
import type { SportFilter } from '@/lib/kalshiTicker/sportFilter';

export interface OpenPositionLite {
  splitCount?: number;
  splitConflictCount?: number;
  splitFeeUnknownCount?: number;
  /** Absent only on stale payloads from a pre-venue relay; those are Kalshi. */
  venue?: 'kalshi' | 'polymarket';
  ticker: string;
  side: 'yes' | 'no';
  netCount: number;
  avgEntryPriceCents: number;
  lastFillAt?: string | null;
  /** Exact basis in cents from the relay ledger; older relays omit it. */
  costBasisCents?: number;
  feesPaidCents?: number;
  /** Opening fees on the OPEN share only (relay-allocated). This — never
   *  feesPaidCents (the whole bucket incl. sold-share + sell fees) — is the
   *  fee component an unrealized recipe subtracts; using the bucket total
   *  double-counts the sold share against realized-net (Jul-14 audit). */
  openFeesCents?: number;
  /** Authoritative lifetime bucket totals. Optional for stale relay payloads;
   * OrgDetail withholds the full-position strip unless all three reconcile. */
  totalBoughtCount?: number;
  totalSoldCount?: number;
  totalBuyCostCents?: number;
}

export interface OrgTraderRow {
  userId: string;
  username: string;
  openCount: number;
  openNotionalCents: number;
  /** Gross trading + settlement P&L before fees. */
  realizedPnlCents: number;
  realizedFeesCents?: number | null;
  realizedPnlNetCents?: number | null;
  openOrderCount?: number | null;
  openPositions?: OpenPositionLite[];
}

interface OrgTradersTableProps {
  orgQueryParam?: string;
  onRowClick?: (row: OrgTraderRow) => void;
  highlightUserId?: string;
  title?: string;
  /** When set, renders an "Org account →" link in the card header — the drill
   *  into the org's full balance + positions/orders view (/positions/org/:id). */
  onViewOrgAccount?: () => void;
}

// Comma-grouping via the shared card formatter (five-figure org P&L).
function formatDollarsSigned(cents: number): string {
  return fmtUsdSigned(cents / 100);
}

export interface OrgTraderUnrealizedResult {
  /** Null unless every open position has a live mark. A partial sum must not
   *  look like a complete account result. */
  valueCents: number | null;
  /** Diagnostic subtotal over the positions that do have marks. Never render
   *  this as the account total when `complete` is false. */
  pricedValueCents: number;
  pricedPositions: number;
  totalPositions: number;
  complete: boolean;
}

/** Match the trader headline: both venues, exact basis, OPEN-share entry
 *  fees, and the Kalshi taker-close estimate. Any missing mark — or an
 *  unknown open-fee allocation — withholds the headline while preserving
 *  explicit marked/total coverage for the UI and tests. Open-share fees
 *  (openFeesCents), not the bucket's feesPaidCents: composing realized-NET
 *  with bucket-total fees double-subtracts the sold share on every
 *  partially-closed position. */
export function computeOrgTraderUnrealized(
  row: OrgTraderRow,
  getKalshiYesPrice: (ticker: string) => number | null,
  getPolyNowPrice: (ticker: string, side: 'yes' | 'no') => number | null =
    (ticker, side) => polyPositionLiveData(ticker, side).nowCents,
  getPolyFeeSchedule: (ticker: string) => PolyFeeSchedule | null | undefined = () => undefined,
): OrgTraderUnrealizedResult {
  const positions = row.openPositions;
  if (!positions || positions.length === 0) {
    return {
      valueCents: 0,
      pricedValueCents: 0,
      pricedPositions: 0,
      totalPositions: 0,
      complete: true,
    };
  }
  let total = 0;
  let pricedPositions = 0;
  for (const p of positions) {
    const venue = p.venue ?? 'kalshi';
    let current: number | null;
    if (venue === 'polymarket') {
      current = getPolyNowPrice(p.ticker, p.side);
    } else {
      const yesPrice = getKalshiYesPrice(p.ticker);
      current = yesPrice == null ? null : p.side === 'yes' ? yesPrice : 100 - yesPrice;
    }
    if (current == null) continue;
    // Unknown open-fee allocation = incomplete, same as a missing mark —
    // withhold rather than price the position as if its entry fees were $0.
    if (p.openFeesCents == null) continue;
    const entryCost =
      (p.costBasisCents ?? p.avgEntryPriceCents * p.netCount) + p.openFeesCents;
    let closingFee = 0;
    if (venue === 'kalshi') {
      const takerRate = takerRateForTicker(p.ticker);
      if (takerRate != null) {
        closingFee = estimateAggregateFeeCents({
          priceCents: current,
          count: p.netCount,
          feeRate: takerRate,
        });
      }
    } else {
      const estimated = estimatePolyOrderFeeCents({
        priceCents: current,
        count: p.netCount,
        role: 'taker',
        schedule: getPolyFeeSchedule(p.ticker),
      });
      if (estimated == null) continue;
      closingFee = estimated;
    }
    pricedPositions++;
    total += current * p.netCount - closingFee - entryCost;
  }
  const complete = pricedPositions === positions.length;
  return {
    valueCents: complete ? total : null,
    pricedValueCents: total,
    pricedPositions,
    totalPositions: positions.length,
    complete,
  };
}

const EMPTY_ROWS: OrgTraderRow[] = [];
const ALL_SPORTS: SportFilter = new Map();

export function OrgTradersTable({
  orgQueryParam = '',
  onRowClick,
  highlightUserId,
  title = 'Org Positions by Trader',
  onViewOrgAccount,
}: OrgTradersTableProps) {
  // #1061: each read result is tagged with the org it was read for, so an
  // org switch hides the outgoing org's rows and error on the very next render.
  const [read, setRead] = useState<{ org: string; rows: OrgTraderRow[]; error: string | null } | null>(null);
  const [reading, setReading] = useState(true);
  // Only the latest read may write; an org switch, a Refresh or unmount makes
  // every earlier reply stale, whichever lands last.
  const readSeq = useRef(0);
  const current = read?.org === orgQueryParam ? read : null;
  const rows = current?.rows ?? EMPTY_ROWS;
  const error = current?.error ?? null;
  const loading = reading || !current;
  const [usRefresh, setUsRefresh] = useState(0);
  const us = useUsOrgRows({ orgQueryParam, ownerFilter: 'all', sportFilter: ALL_SPORTS,
    refreshKey: usRefresh, economics: true, includeFills: false });
  const { current: usCurrent, marks: usMarks, nowMs: usNowMs } = us;
  const usMembers = useMemo(() => {
    return new Map(rows.map(row => [row.userId, usOrgMemberEconomics(usCurrent, row.userId, usMarks, usNowMs)]));
  }, [rows, usCurrent, usMarks, usNowMs]);
  const venues = us.current?.accounts === 'absent' ? 'K+P' : 'K+P+US';
  // Same masking the positions tiles honor (default hidden) — without this a
  // manager sees ••• tiles above a table of everyone's live P&L dollars.
  const showPnl = useDisplaySettings((s) => s.showPnl);
  // Consumer feed filters (feed-filters-per-view): every open position in the
  // org, by identity, so the manager's marks ride the edge at browser cadence
  // without the connection carrying the sports they sit in. Undeclared until
  // the first fetch lands (null) — a loading table has nothing to name.
  const orgFeedIds = useMemo(
    () => (loading && rows.length === 0 ? null : rows.flatMap((row) => (row.openPositions ?? []).map((p) => p.ticker))),
    [rows, loading],
  );
  useTickerFeedNeeds('org-traders-table', orgFeedIds, { fold: 'view' });
  const polyFeeConditionIds = useMemo(
    () =>
      rows.flatMap((row) =>
        (row.openPositions ?? [])
          .filter((position) => position.venue === 'polymarket')
          .map((position) => position.ticker),
      ),
    [rows],
  );
  const { schedules: polyFeeSchedules } = usePolyFeeSchedules(polyFeeConditionIds);

  // Re-derive Unrealized P&L when hydra ticks update prices.
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  void marketVersion;

  const fetchRows = useCallback(async () => {
    const seq = ++readSeq.current;
    const org = orgQueryParam;
    setReading(true);
    // A refresh of the same org keeps its rows; a different org starts empty.
    setRead((prev) => (prev?.org === org ? { ...prev, error: null } : null));
    let next: (prev: typeof read) => NonNullable<typeof read>;
    try {
      const res = await apiFetch(`/api/manager/org/positions-by-trader${org}`);
      const data = await res.json();
      next = res.ok
        ? () => ({ org, rows: data.rows ?? [], error: null })
        : () => ({ org, rows: [], error: data?.error ?? 'Failed to load' });
    } catch {
      next = (prev) => ({ org, rows: prev?.org === org ? prev.rows : [], error: 'Network error' });
    }
    if (seq !== readSeq.current) return;
    setRead(next);
    setReading(false);
  }, [orgQueryParam]);

  useEffect(() => {
    // The sequence itself (not a DOM node): unmount supersedes any read in flight.
    const reads = readSeq;
    fetchRows();
    return () => { reads.current++; };
  }, [fetchRows]);

  return (
    <div className="mt-6 space-y-2">
      {/* Section header in the page background — matches the Positions / Open
          Orders / Bots headers on this page; the card below is just the table. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <h2 className="text-foreground text-sm font-semibold">{title}</h2>
          <span className="text-muted-foreground text-[10px]">{venues} totals</span>
          <InfoTooltip title="Trader totals">
            Totals include each trader's recorded Kalshi, international Polymarket and Polymarket US activity. A missing ledger, basis, fee or current quote leaves its total unavailable. Open Orders covers Kalshi only.
          </InfoTooltip>
        </div>
        <div className="flex items-center gap-3">
          {onViewOrgAccount && (
            <button
              onClick={onViewOrgAccount}
              title="Open this org's account (balance + all positions/orders)"
              className="text-muted-foreground hover:text-primary text-[11px] underline-offset-2 hover:underline"
            >
              Org account →
            </button>
          )}
          <button
            onClick={() => { fetchRows(); setUsRefresh(n => n + 1); }}
            title="Refresh"
            className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]"
          >
            <RefreshCw size={10} /> Refresh
          </button>
        </div>
      </div>
      {us.current?.accounts !== 'absent' && (us.current?.accounts !== 'present' || !us.current.positions?.complete) && (
        <p className="text-muted-foreground text-xs" role="status">{!us.current ? 'Checking Polymarket US totals…' : 'Polymarket US totals incomplete.'}</p>
      )}
      <div className="bg-card border-border overflow-x-auto rounded-lg border">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
          </div>
        ) : error ? (
          <p className="text-muted-foreground px-4 py-4 text-xs">{error}</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground px-4 py-4 text-xs">No activity yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-[11px] font-medium uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Member</th>
                <th className="px-3 py-2 text-right" title={`Open contracts (${venues})`}>Open Contracts</th>
                <th className="px-3 py-2 text-right">Open Orders (K)</th>
                <th className="px-3 py-2 text-right" title={`Open entry basis (${venues})`}>Open Notional</th>
                <th className="px-3 py-2 text-right">Unrealized ({venues})</th>
                <th
                  className="px-3 py-2 text-right"
                  title="Trading and settlement P&L before fees"
                >
                  Realized Gross
                </th>
                <th
                  className="px-3 py-2 text-right"
                  title="Actual recorded fees allocated to sold and settled contracts"
                >
                  Realized Fees
                </th>
                <th
                  className="px-3 py-2 text-right"
                  title="Realized Gross minus Realized Fees; dash means contributing fee data is incomplete"
                >
                  Realized Net
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isHighlight = r.userId === highlightUserId;
                const clickable = !!onRowClick;
                const kpUnrealized = computeOrgTraderUnrealized(
                  r,
                  getMarketPrice,
                  undefined,
                  (ticker) => polyFeeSchedules.get(ticker),
                );
                const usSummary = usMembers.get(r.userId)!;
                const unrealized = { valueCents: addKnown(kpUnrealized.valueCents, usSummary.unrealizedCents),
                  pricedPositions: kpUnrealized.pricedPositions + usSummary.pricedPositions,
                  totalPositions: kpUnrealized.totalPositions + usSummary.totalPositions,
                  complete: kpUnrealized.complete && usSummary.unrealizedCents !== null };
                const count = addKnown(r.openCount, usSummary.openCount);
                const notional = addKnown(r.openNotionalCents, usSummary.notionalCents);
                const gross = addKnown(r.realizedPnlCents, usSummary.realizedGrossCents);
                const fees = addKnown(r.realizedFeesCents, usSummary.realizedFeesCents);
                const net = addKnown(r.realizedPnlNetCents, usSummary.realizedNetCents);
                return (
                  <tr
                    key={r.userId}
                    onClick={clickable ? () => onRowClick(r) : undefined}
                    className={`border-border/40 border-b ${
                      clickable ? 'hover:bg-muted/30 cursor-pointer' : ''
                    } ${isHighlight ? 'bg-muted/40' : ''}`}
                  >
                    <td className="text-foreground px-4 py-2">
                      {r.username}
                      {isHighlight && (
                        <span className="text-muted-foreground ml-2 text-[10px]">(you)</span>
                      )}
                    </td>
                    <td className="text-foreground px-3 py-2 text-right font-mono">{count ?? '—'}</td>
                    <td className="text-foreground px-3 py-2 text-right font-mono">
                      <span title={r.openOrderCount == null ? 'Open-order count unavailable' : undefined}>{r.openOrderCount ?? '\u2014'}</span>
                    </td>
                    <td className="text-foreground px-3 py-2 text-right font-mono">
                      {notional === null ? '—' : fmtUsd(notional / 100)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-medium ${
                        !showPnl || unrealized.valueCents == null
                          ? 'text-muted-foreground'
                          : unrealized.valueCents >= 0
                            ? 'text-green-500'
                            : 'text-red-500'
                      }`}
                      title={
                        unrealized.complete
                          ? `${unrealized.pricedPositions}/${unrealized.totalPositions} positions fully priced`
                          : `Unrealized P&L withheld: ${unrealized.pricedPositions}/${unrealized.totalPositions} positions have both a live mark and close-fee schedule`
                      }
                    >
                      {unrealized.valueCents == null
                        ? `— (${unrealized.pricedPositions}/${unrealized.totalPositions})`
                        : showPnl
                          ? formatDollarsSigned(unrealized.valueCents)
                          : '•••'}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-medium ${
                        !showPnl || gross === null
                          ? 'text-muted-foreground'
                          : gross >= 0
                            ? 'text-green-500'
                            : 'text-red-500'
                      }`}
                    >
                      {!showPnl ? '•••' : gross === null ? '—' : formatDollarsSigned(gross)}
                    </td>
                    <td className="text-muted-foreground px-3 py-2 text-right font-mono">
                      {fees === null
                        ? '—'
                        : showPnl
                          ? fmtUsd(fees / 100)
                          : '•••'}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-medium ${
                        !showPnl || net === null
                          ? 'text-muted-foreground'
                          : net >= 0
                            ? 'text-green-500'
                            : 'text-red-500'
                      }`}
                    >
                      {net === null
                        ? '—'
                        : showPnl
                          ? formatDollarsSigned(net)
                          : '•••'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
